import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { qr } from '../api';

const ProgramPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    qr.getProgram(token)
      .then((res) => setData(res.data))
      .catch(() => setError('Ugyldig eller utløpt QR-kode'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const getTypeIcon = (type) => {
    if (type === 'pause') return '☕';
    if (type === 'info') return 'ℹ️';
    return '🎤';
  };

  const getTypeStyle = (type) => {
    if (type === 'pause') return 'border-l-4 border-gold-500 bg-amber-50';
    if (type === 'info') return 'border-l-4 border-navy-400 bg-blue-50';
    return 'border-l-4 border-transparent bg-white';
  };

  const groupByDate = (presentations) => {
    const groups = {};
    presentations.forEach((p) => {
      const date = p.event_date ? p.event_date.split('T')[0] : 'ingen-dato';
      if (!groups[date]) groups[date] = [];
      groups[date].push(p);
    });
    return groups;
  };

  const formatDayHeader = (dateStr) => {
    if (dateStr === 'ingen-dato') return null;
    return new Date(dateStr).toLocaleDateString('nb-NO', {
      weekday: 'long', day: 'numeric', month: 'long'
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-cream-50">
      <p className="text-navy-400">Laster program...</p>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-screen bg-cream-50">
      <p className="text-red-500">{error}</p>
    </div>
  );

  const groups = groupByDate(data.presentations);
  const isMultiDay = Object.keys(groups).filter(k => k !== 'ingen-dato').length > 1;

  return (
    <div className="min-h-screen bg-cream-50">
      <div className="bg-navy-800 text-white px-5 py-6">
        <div className="max-w-lg mx-auto">
          <p className="text-gold-400 text-xs font-semibold uppercase tracking-widest mb-1">PODIUM</p>
          <h1 className="text-2xl font-bold text-cream-100">{data.conference.title}</h1>
          <p className="text-navy-300 mt-1 text-sm">
            {new Date(data.conference.date).toLocaleDateString('nb-NO', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            })}
          </p>
          {data.conference.location && (
            <p className="text-navy-300 text-sm mt-0.5">📍 {data.conference.location}</p>
          )}
          <p className="text-4xl font-mono font-bold mt-4 text-gold-400">
            {time.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5">
        {Object.entries(groups).map(([date, items]) => (
          <div key={date} className="mb-6">
            {isMultiDay && formatDayHeader(date) && (
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-navy-200"></div>
                <p className="text-navy-600 text-sm font-bold uppercase tracking-wide capitalize">
                  {formatDayHeader(date)}
                </p>
                <div className="flex-1 h-px bg-navy-200"></div>
              </div>
            )}
            <div className="grid gap-2">
              {items.map((p, index) => (
                <div key={index} className={`rounded-xl shadow-sm p-4 ${getTypeStyle(p.type)}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex gap-3 items-start">
                      <span className="text-lg mt-0.5">{getTypeIcon(p.type)}</span>
                      <div>
                        <p className="font-semibold text-navy-800">{p.title}</p>
                        {p.type === 'presentation' && p.speaker_name && (
                          <p className="text-sm text-navy-500 mt-0.5">
                            {p.speaker_name}{p.speaker_organization && ` · ${p.speaker_organization}`}
                          </p>
                        )}
                        {p.description && (
                          <p className="text-sm text-navy-400 mt-1">{p.description}</p>
                        )}
                      </div>
                    </div>
                    {p.start_time && (
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-navy-700">{p.start_time.slice(0, 5)}</p>
                        {p.end_time && <p className="text-xs text-navy-400">- {p.end_time.slice(0, 5)}</p>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <p className="text-center text-xs text-navy-300 mt-4 tracking-wide">PODIUM · Konferanseverktøy</p>
      </div>
    </div>
  );
};

export default ProgramPage;
